import type {Classification} from '@/api/classifications';
import {isPolicyViolationType} from '@/lib/classificationType';

export type RiskTreeNode = {
  id: string;
  label: string;
  // "Non-Violative > Harassment" - the same risk name can sit under several policy violations, so a
  // chip or search hit needs the branch to say which one it means.
  path: string;
  children: RiskTreeNode[];
};

export type RiskTree = {
  roots: RiskTreeNode[];
  byId: Map<string, RiskTreeNode>;
};

const MAX_DEPTH = 32;

export function buildRiskTree(classifications: readonly Classification[]): RiskTree {
  const byParent = new Map<string | null, Classification[]>();
  for (const item of classifications) {
    const key = isPolicyViolationType(item.type) ? null : item.parentId;
    byParent.set(key, [...(byParent.get(key) ?? []), item]);
  }

  const byId = new Map<string, RiskTreeNode>();
  const build = (item: Classification, parentPath: string, depth: number): RiskTreeNode => {
    const label = item.displayName || item.name;
    const path = parentPath ? `${parentPath} > ${label}` : label;
    const node: RiskTreeNode = {
      id: item.id,
      label,
      path,
      // Bounded so a malformed cycle in the data cannot recurse forever.
      children:
        depth >= MAX_DEPTH
          ? []
          : (byParent.get(item.id) ?? [])
              .map((child) => build(child, path, depth + 1))
              .sort((a, b) => a.label.localeCompare(b.label))
    };
    byId.set(node.id, node);
    return node;
  };

  const roots = (byParent.get(null) ?? [])
    .map((root) => build(root, '', 0))
    .sort((a, b) => a.label.localeCompare(b.label));
  return {roots, byId};
}

export function leafIds(node: RiskTreeNode): string[] {
  return node.children.length === 0 ? [node.id] : node.children.flatMap(leafIds);
}

export type CheckState = 'checked' | 'indeterminate' | 'unchecked';

// `selected` holds leaf ids only, so a parent's state is just a count of its leaves.
export function checkState(node: RiskTreeNode, selected: ReadonlySet<string>): CheckState {
  const leaves = leafIds(node);
  const count = leaves.filter((id) => selected.has(id)).length;
  if (count === 0) {
    return 'unchecked';
  }

  return count === leaves.length ? 'checked' : 'indeterminate';
}

// Checking a parent selects its whole subtree; unchecking one clears it.
export function toggleNode(node: RiskTreeNode, selected: readonly string[]): string[] {
  const leaves = leafIds(node);
  const current = new Set(selected);
  if (checkState(node, current) === 'checked') {
    return selected.filter((id) => !leaves.includes(id));
  }

  return [...new Set([...selected, ...leaves])];
}

// The ids to send: a fully selected subtree collapses to its topmost node, so choosing a whole
// vertical sends one id, not every leaf under it.
export function topmostSelected(tree: RiskTree, selected: readonly string[]): string[] {
  const set = new Set(selected);
  const out: string[] = [];
  const visit = (node: RiskTreeNode) => {
    const state = checkState(node, set);
    if (state === 'checked') {
      out.push(node.id);
    } else if (state === 'indeterminate') {
      for (const child of node.children) {
        visit(child);
      }
    }
  };

  for (const root of tree.roots) {
    visit(root);
  }

  return out;
}
